import { Box, Button, Card, CardContent, Chip, Divider, Grid, TextField, Tooltip, Typography } from '@mui/material';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import 'chart.js/auto';
import { format, startOfMonth } from "date-fns";
import { ptBR } from 'date-fns/locale';
import React, { useEffect, useState } from 'react';
import { Bar } from 'react-chartjs-2';
import { useNavigate } from "react-router-dom";
import api from '../api';
import DidNotLoad from "../components/DidNotLoad";
import Loading from "../components/Loading";
import PremiumFeatureBadge from "../components/PremiumFeatureBadge";
import { formatCurrencyInCents } from "../helpers/formatters";
import { formatFilamentGrams, formatProductionDurationMinutes } from "../helpers/jobs";
import { patchJobsSummary } from "../helpers/jobLivePatch";
import { applyJobLiveNotification } from "../helpers/jobLiveInProgress";
import { isJobIntegrationLiveNotification, subscribeLiveNotifications } from "../helpers/liveNotifications";
import { hasPremiumAccess, PREMIUM_FEATURE_TOOLTIP, readStoredPlanName } from "../helpers/subscription";

function Dashboard() {
    const initialData = {
        printers_totals: {
            total: 0,
            total_amount: 0,
        },
        materials_total: {
            total: 0,
            total_amount: 0,
        },
        products_total: 0,
        checkouts_total: 0,
        orders_total: 0,
        orders_amount: {
            total_amount: 0,
            cash_amount: 0,
            pix_amount: 0,
            credit_card_amount: 0,
            debit_card_amount: 0,
            bank_transfer_amount: 0,
            barter_amount: 0,
            pending_payment: 0,
        },
        checkouts_amount: 0,
        sales_by_channel_amount: {
            channels: [],
        },
        sales_by_day_amount: {
            sales: [],
        },
        customers_total: 0,
        transactions_summary: {
            total_income: 0,
            total_outcome: 0,
            net_amount: 0,
            total_transactions: 0,
        },
        jobs_summary: {
            total: 0,
            total_objects: 0,
            in_progress: 0,
            completed: 0,
            issues: 0,
            total_duration_minutes: 0,
            total_filament_grams: 0,
            total_printed_objects: 0,
            success_rate_objects: 0,
            top_printer: null,
        },
    }
    const [data, setData] = useState(initialData);

    const [value, setValue] = useState(() => {
        const today = new Date();
        return [startOfMonth(today), today];
    });
    const [dateFilterApplied, setDateFilterApplied] = useState(true);
    const [isLoading, setIsLoading] = useState(true); // Estado para controlar o carregamento
    const [didNotLoad, setDidNotLoad] = useState(false);
    const navigate = useNavigate();
    const hasPremium = hasPremiumAccess(readStoredPlanName());

    const handleDateRange = () => {
        if (value[0] && value[1]) {
            return `${value[0].toLocaleDateString('pt-BR')} - ${value[1].toLocaleDateString('pt-BR')}`;
        }
        return 'Nenhuma data selecionada';
    };

    const fetchData = (startDate, endDate) => {
        setIsLoading(true);
        const start = startDate ? format(startDate, 'yyyy-MM-dd') : '';
        const end = endDate ? format(endDate, 'yyyy-MM-dd') : '';

        const loadPromise = api.get('dashboard/', {
                params: start && end ? { start_date: start, end_date: end } : {},
            }).then((response) => {
                const payload = response.data.data ?? response.data;
                if (typeof payload === 'undefined') {
                    throw new Error('Resposta sem dados do dashboard.');
                }
                return payload;
            });

        loadPromise
            .then((payload) => {
                setData(payload);
                setDidNotLoad(false);
            })
            .catch(() => {
                setDidNotLoad(true);
            })
            .finally(() => {
                setIsLoading(false);
            });
    };

    useEffect(() => {
        fetchData(value[0], value[1]);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        return subscribeLiveNotifications((detail) => {
            if (!isJobIntegrationLiveNotification(detail)) {
                return;
            }
            applyJobLiveNotification(detail);
            setData((prev) => ({
                ...prev,
                jobs_summary: patchJobsSummary(prev.jobs_summary, detail),
            }));
        });
    }, []);

    // Aplica o filtro com as novas datas
    const handleApplyFilter = () => {
        if (value[0] && value[1]) {
            setDateFilterApplied(true);
        }
        fetchData(value[0], value[1]);
    };
    const handleResetFilter = () => {
        value[0] = null;
        value[1] = null;
        setDateFilterApplied(false);
        fetchData(value[0], value[1]);
    };

    const channelPalette = ['#36A2EB', '#4BC0C0', '#FFCE56', '#9966FF', '#FF6384', '#FF9F40', '#78e08f', '#C9CBCF'];
    const salesChannelRows = Array.isArray(data.sales_by_channel_amount?.channels)
        ? data.sales_by_channel_amount.channels
        : [];
    const salesByChannelData = {
        labels: salesChannelRows.map((row) => row.name),
        datasets: [
            {
                label: 'Vendas por Canal (R$)',
                data: salesChannelRows.map((row) => (Number(row.amount) || 0) / 100),
                backgroundColor: salesChannelRows.map((_, index) => channelPalette[index % channelPalette.length]),
            },
        ],
    };

    const salesByDayData = {
        labels: data.sales_by_day_amount.sales.map(sale => {
            // Format date from YYYY-MM-DD to DD/MM
            const [year, month, day] = sale.date.split('-');
            return `${day}/${month}`;
        }),
        datasets: [
            {
                label: 'Vendas por Dia (R$)',
                data: data.sales_by_day_amount.sales.map(sale => sale.amount / 100),
                backgroundColor: '#36A2EB',
                borderColor: '#36A2EB',
                borderWidth: 1,
                fill: false,
                tension: 0.1,
            },
        ],
    };

    const salesData = {
        labels: ['Crédito', 'PIX', 'Dinheiro', 'Débito', 'Transferência', 'Pendente'],
        datasets: [
            {
                label: 'Vendas por Tipo (R$)',
                data: [
                    (data.orders_amount.credit_card_amount / 100),
                    (data.orders_amount.pix_amount / 100),
                    (data.orders_amount.cash_amount / 100),
                    (data.orders_amount.debit_card_amount / 100),
                    (data.orders_amount.bank_transfer_amount / 100),
                    (data.orders_amount.pending_payment / 100),
                ],
                backgroundColor: ['#36A2EB', '#4BC0C0', '#FFCE56', '#9966FF', '#78e08f', '#FF6384'],
            },
        ],
    };

    const moneyBarOptions = {
        responsive: true,
        maintainAspectRatio: false,
        scales: {
            x: {
                ticks: {
                    maxRotation: 45,
                    minRotation: 0,
                },
            },
            y: {
                beginAtZero: true,
                ticks: {
                    callback(value) {
                        return `R$ ${Number(value).toFixed(2).replace('.', ',')}`;
                    },
                },
            },
        },
        plugins: {
            tooltip: {
                callbacks: {
                    label(context) {
                        return `R$ ${Number(context.parsed.y).toFixed(2).replace('.', ',')}`;
                    },
                },
            },
            legend: {
                display: false,
            },
        },
    };

    const jobsTotalInPeriod = data.jobs_summary?.total ?? (
        (data.jobs_summary?.in_progress ?? 0)
        + (data.jobs_summary?.completed ?? 0)
        + (data.jobs_summary?.issues ?? 0)
    );
    const jobsObjectsInPeriod = data.jobs_summary?.total_objects ?? 0;
    const jobsSuccessRate = Number(data.jobs_summary?.success_rate_objects) || 0;
    const jobsDurationMinutes = data.jobs_summary?.total_duration_minutes ?? 0;
    const jobsFilamentGrams = data.jobs_summary?.total_filament_grams ?? 0;
    const topPrinter = data.jobs_summary?.top_printer;

    if (isLoading) {
        return <Loading />;
    }

    if (didNotLoad) {
        return <DidNotLoad />
    }

    return (
        <Box padding={2}>
            {value[0] && value[1] && dateFilterApplied && (
                <Typography variant="h6" gutterBottom>
                    Período apurado: {handleDateRange()}
                </Typography>
            )}

            <LocalizationProvider dateAdapter={AdapterDateFns} locale={ptBR}>
                <Grid container spacing={2} alignItems="flex-end">
                    <Grid item>
                        <Typography variant="subtitle1">Data de Início</Typography>
                        <DatePicker
                            value={value[0]}
                            onChange={(newValue) => setValue([newValue, value[1]])}
                            renderInput={(params) => <TextField {...params} />}
                        />
                    </Grid>
                    <Grid item>
                        <Typography variant="subtitle1">Data de Fim</Typography>
                        <DatePicker
                            value={value[1]}
                            onChange={(newValue) => setValue([value[0], newValue])}
                            renderInput={(params) => <TextField {...params} />}
                        />
                    </Grid>
                    <Grid item>
                        <Button variant="contained" color="primary" mt={4} onClick={handleApplyFilter}>
                            Aplicar Filtro
                        </Button>
                    </Grid>
                    <Grid item>
                        <Button variant="contained" color="secondary" mt={4} onClick={handleResetFilter}>
                            Ver tudo
                        </Button>
                    </Grid>
                </Grid>
            </LocalizationProvider>

            {/* Adicionando margem abaixo do intervalo de datas */}
            <Box mb={4} /> {/* Este Box aplica uma margem de 4 unidades de espaçamento abaixo */}

            {/* Card de Faturamento */}
            <Grid item xs={12}>
                <Tooltip title={!hasPremium ? PREMIUM_FEATURE_TOOLTIP : ''} arrow disableHoverListener={hasPremium}>
                    <Card
                        onClick={hasPremium ? () => navigate('/admin/transactions') : undefined}
                        sx={{
                            cursor: hasPremium ? "pointer" : "default",
                            opacity: hasPremium ? 1 : 0.88,
                            transition: "0.3s",
                            "&:hover": hasPremium ? {
                                boxShadow: 6,
                            } : {},
                        }}
                    >
                        <CardContent>
                            <Typography
                                variant="h6"
                                gutterBottom
                                sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}
                            >
                                💰 Faturamento do Período
                                <PremiumFeatureBadge />
                            </Typography>
                        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 3, alignItems: 'center' }}>
                            {/* Faturamento (valor de vendas) */}
                            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flex: 1 }}>
                                <Typography variant="body2" color="text.secondary" gutterBottom>
                                    Faturamento
                                </Typography>
                                <Chip
                                    label={formatCurrencyInCents(data.orders_amount.total_amount)}
                                    color="success"
                                    size="large"
                                    sx={{ fontSize: '1.1rem', fontWeight: 'bold' }}
                                />
                            </Box>

                            <Divider orientation="vertical" flexItem sx={{ display: { xs: 'none', md: 'block' } }} />
                            <Divider sx={{ display: { xs: 'block', md: 'none' } }} />

                            {/* Recebimentos (Líquido) - Centro */}
                            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flex: 1 }}>
                                <Typography variant="body2" color="text.secondary" gutterBottom>
                                    Recebimentos (Líquido)
                                </Typography>
                                <Chip
                                    label={formatCurrencyInCents(data.transactions_summary.net_amount)}
                                    color={data.transactions_summary.net_amount >= 0 ? "success" : "error"}
                                    variant="filled"
                                    size="large"
                                    sx={{ fontSize: '1.1rem', fontWeight: 'bold' }}
                                />
                            </Box>

                            <Divider orientation="vertical" flexItem sx={{ display: { xs: 'none', md: 'block' } }} />
                            <Divider sx={{ display: { xs: 'block', md: 'none' } }} />

                            {/* Saídas (Despesas) - Direita */}
                            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flex: 1 }}>
                                <Typography variant="body2" color="text.secondary" gutterBottom>
                                    Saídas
                                </Typography>
                                <Chip
                                    label={formatCurrencyInCents(data.transactions_summary.total_outcome)}
                                    color="error"
                                    size="medium"
                                    variant="outlined"
                                    sx={{ fontSize: '0.9rem' }}
                                />
                            </Box>
                        </Box>
                        <Typography variant="caption" color="text.secondary" align="center" sx={{ mt: 2, display: 'block' }}>
                            {data.transactions_summary.total_transactions} transações no período
                        </Typography>
                    </CardContent>
                </Card>
                </Tooltip>
            </Grid>

            <Box mb={4} />

            <Grid container spacing={4}>
                <Grid item xs={12} md={6}>
                    <Card>
                        <CardContent>
                            <Typography variant="h6" gutterBottom>
                                Vendas por canal
                            </Typography>
                            <div style={{ height: '320px', position: 'relative' }}>
                                {salesChannelRows.length > 0 ? (
                                    <Bar data={salesByChannelData} options={moneyBarOptions} />
                                ) : (
                                    <Typography variant="body2" color="text.secondary">
                                        Nenhuma venda com canal no período.
                                    </Typography>
                                )}
                            </div>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item xs={12} md={6}>
                    <Card>
                        <CardContent>
                            <Typography variant="h6" gutterBottom>
                                Vendas por tipo
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                Total: {formatCurrencyInCents(data.orders_amount.total_amount)}
                            </Typography>
                            <div style={{ height: '320px', position: 'relative' }}>
                                <Bar data={salesData} options={moneyBarOptions} />
                            </div>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item xs={12}>
                    <Card>
                        <CardContent>
                            <Typography variant="h6" gutterBottom>
                                Vendas por dia
                            </Typography>
                            <div style={{ height: '400px', position: 'relative' }}>
                                <Bar data={salesByDayData} options={moneyBarOptions} />
                            </div>
                        </CardContent>
                    </Card>
                </Grid>

                <Grid item xs={12} md={6}>
                    <Card
                        onClick={() => navigate('/admin/checkouts')}
                        sx={{
                            cursor: "pointer",
                            transition: "0.3s",
                            "&:hover": {
                                boxShadow: 6,
                            },
                        }}
                    >
                        <CardContent>
                            <Typography variant="h6">Vendas em andamento</Typography>
                            <Typography variant="h4">{data.checkouts_total}</Typography>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item xs={12} md={6}>
                    <Card
                        onClick={() => navigate('/admin/orders')}
                        sx={{
                            cursor: "pointer",
                            transition: "0.3s",
                            "&:hover": {
                                boxShadow: 6,
                            },
                        }}
                    >
                        <CardContent>
                            <Typography variant="h6">Vendas finalizadas</Typography>
                            <Typography variant="h4">{data.orders_total}</Typography>
                        </CardContent>
                    </Card>
                </Grid>

                <Grid item xs={12}>
                    <Typography variant="h6" sx={{ mb: 1.5 }}>
                        Produção no período
                    </Typography>
                    <Card sx={{ mb: 2 }}>
                        <CardContent>
                            <Grid container spacing={2}>
                                <Grid item xs={6} md={3}>
                                    <Typography variant="body2" color="text.secondary">Tempo de impressão (estimado)</Typography>
                                    <Typography variant="h5" fontWeight={800}>
                                        {formatProductionDurationMinutes(jobsDurationMinutes)}
                                    </Typography>
                                </Grid>
                                <Grid item xs={6} md={3}>
                                    <Typography variant="body2" color="text.secondary">Filamento (estimado)</Typography>
                                    <Typography variant="h5" fontWeight={800}>
                                        {formatFilamentGrams(jobsFilamentGrams)}
                                    </Typography>
                                </Grid>
                                <Grid item xs={6} md={3}>
                                    <Typography variant="body2" color="text.secondary">Sucesso em objetos</Typography>
                                    <Typography variant="h5" fontWeight={800}>
                                        {jobsSuccessRate.toFixed(1).replace('.', ',')}%
                                    </Typography>
                                </Grid>
                                <Grid item xs={6} md={3}>
                                    <Typography variant="body2" color="text.secondary">Impressora mais utilizada</Typography>
                                    <Typography variant="h6" fontWeight={700}>
                                        {topPrinter?.name || '—'}
                                    </Typography>
                                    {topPrinter?.name && (
                                        <Typography variant="caption" color="text.secondary">
                                            {formatProductionDurationMinutes(topPrinter.total_duration_minutes)}
                                            {' · '}
                                            {topPrinter.duration_share_percent?.toFixed?.(0) ?? topPrinter.duration_share_percent}%
                                            {' do tempo'}
                                            {' · '}
                                            {topPrinter.total_jobs} impressões
                                        </Typography>
                                    )}
                                </Grid>
                            </Grid>
                        </CardContent>
                    </Card>

                    <Typography variant="h6" sx={{ mb: 1.5 }}>
                        {jobsTotalInPeriod} impressões · {jobsObjectsInPeriod} objetos no período
                    </Typography>
                    <Grid container spacing={2}>
                        <Grid item xs={12} md={4}>
                            <Card
                                onClick={() => navigate('/admin/jobs')}
                                sx={{
                                    cursor: "pointer",
                                    transition: "0.3s",
                                    "&:hover": {
                                        boxShadow: 6,
                                    },
                                }}
                            >
                                <CardContent>
                                    <Typography variant="h6">Impressões em andamento</Typography>
                                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                                        Impressões que estão acontecendo
                                    </Typography>
                                    <Typography variant="h4">{data.jobs_summary?.in_progress ?? 0}</Typography>
                                </CardContent>
                            </Card>
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <Card
                                onClick={() => navigate('/admin/jobs')}
                                sx={{
                                    cursor: "pointer",
                                    transition: "0.3s",
                                    "&:hover": {
                                        boxShadow: 6,
                                    },
                                }}
                            >
                                <CardContent>
                                    <Typography variant="h6">Impressões concluídas</Typography>
                                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                                        Impressões que terminaram sem qualquer tipo de falha
                                    </Typography>
                                    <Typography variant="h4">{data.jobs_summary?.completed ?? 0}</Typography>
                                </CardContent>
                            </Card>
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <Card
                                onClick={() => navigate('/admin/jobs')}
                                sx={{
                                    cursor: "pointer",
                                    transition: "0.3s",
                                    "&:hover": {
                                        boxShadow: 6,
                                    },
                                }}
                            >
                                <CardContent>
                                    <Typography variant="h6">Impressões com erro</Typography>
                                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                                        Falha, cancelada, parcial ou fator externo
                                    </Typography>
                                    <Typography variant="h4">{data.jobs_summary?.issues ?? 0}</Typography>
                                </CardContent>
                            </Card>
                        </Grid>
                    </Grid>
                </Grid>

                <Grid item xs={12}>
                    {value[0] && value[1] && dateFilterApplied && (
                        <Typography gutterBottom>
                            As quantidades apresentadas referem-se somente ao período filtrado: {handleDateRange()}
                        </Typography>
                    )}
                </Grid>

                <Grid item xs={12}>
                    <Divider sx={{ my: 2 }} /> {/* my adiciona margem vertical */}
                    <Typography variant="h6" gutterBottom>
                        Geral
                    </Typography>
                </Grid>


                <Box mb={4} /> {/* Este Box aplica uma margem de 4 unidades de espaçamento abaixo */}

                <Grid item xs={12} md={3}>
                    <Card
                        onClick={() => navigate('/admin/customers')}
                        sx={{
                            cursor: "pointer",
                            transition: "0.3s",
                            "&:hover": {
                                boxShadow: 6,
                            },
                        }}
                    >
                        <CardContent>
                            <Typography variant="h6">Clientes</Typography>
                            <Typography variant="h5">{data.customers_total}</Typography>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item xs={12} md={3}>
                    <Card
                        onClick={() => navigate('/admin/products')}
                        sx={{
                            cursor: "pointer",
                            transition: "0.3s",
                            "&:hover": {
                                boxShadow: 6,
                            },
                        }}
                    >
                        <CardContent>
                            <Typography variant="h6">Produtos</Typography>
                            <Typography variant="h5">{data.products_total}</Typography>
                        </CardContent>
                    </Card>
                </Grid>



                {/* Cards de resumo */}
                <Grid item xs={12} md={3}>
                    <Card
                        onClick={() => navigate('/admin/printers')}
                        sx={{
                            cursor: "pointer",
                            transition: "0.3s",
                            "&:hover": {
                                boxShadow: 6,
                            },
                        }}
                    >
                        <CardContent>
                            <Typography variant="h6">Impressoras</Typography>
                            <Typography variant="h5">{data.printers_totals.total} ({formatCurrencyInCents(data.printers_totals.total_amount)})</Typography>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item xs={12} md={3}>
                    <Card
                        onClick={() => navigate('/admin/materials')}
                        sx={{
                            cursor: "pointer",
                            transition: "0.3s",
                            "&:hover": {
                                boxShadow: 6,
                            },
                        }}
                    >
                        <CardContent>
                            <Typography variant="h6">Filamentos</Typography>
                            <Typography variant="h5">{data.materials_total.total} ({formatCurrencyInCents(data.materials_total.total_amount)})</Typography>
                        </CardContent>
                    </Card>
                </Grid>


                {/*<Grid item xs={12}>*/}
                {/*    <Divider sx={{ my: 2 }} /> /!* my adiciona margem vertical *!/*/}
                {/*</Grid>*/}

                {/*<Grid item xs={12} md={4}>*/}
                {/*    <Card>*/}
                {/*        <CardContent>*/}
                {/*            <Typography variant="h6">Encomendas</Typography>*/}
                {/*            <Typography variant="h4">Em breve*</Typography>*/}
                {/*        </CardContent>*/}
                {/*    </Card>*/}
                {/*</Grid>*/}
                {/*<Grid item xs={12} md={4}>*/}
                {/*    <Card>*/}
                {/*        <CardContent>*/}
                {/*            <Typography variant="h6">Orçamentos em andamento</Typography>*/}
                {/*            <Typography variant="h4">Em breve*</Typography>*/}
                {/*        </CardContent>*/}
                {/*    </Card>*/}
                {/*</Grid>*/}
                {/*<Grid item xs={12} md={4}>*/}
                {/*    <Card>*/}
                {/*        <CardContent>*/}
                {/*            <Typography variant="h6">Orçamentos aprovados</Typography>*/}
                {/*            <Typography variant="h4">Em breve*</Typography>*/}
                {/*        </CardContent>*/}
                {/*    </Card>*/}
                {/*</Grid>*/}
                {/*<Grid item xs={12}>*/}
                {/*    <Typography variant="h6">* Funcionalidade disponível para os planos pagos</Typography>*/}
                {/*</Grid>*/}
            </Grid>
        </Box>
    );
}

export default Dashboard;
